import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { MapPin, Clock, Percent } from "lucide-react";

export default function Visits() {
    const [visits, setVisits] = useState([]);
    useEffect(() => {
        http.get("/visits", { params: { limit: 200 } }).then((r) => setVisits(r.data));
    }, []);

    const stats = useMemo(() => {
        if (!visits.length) return { avgScore: 0, avgShelf: 0, avgDur: 0 };
        const s = visits.reduce(
            (a, v) => ({
                score: a.score + v.checklist_score,
                shelf: a.shelf + v.shelf_share,
                dur: a.dur + v.duration_minutes,
            }),
            { score: 0, shelf: 0, dur: 0 }
        );
        return {
            avgScore: (s.score / visits.length).toFixed(1),
            avgShelf: (s.shelf / visits.length).toFixed(1),
            avgDur: Math.round(s.dur / visits.length),
        };
    }, [visits]);

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Retail Execution</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Field Visits & Compliance</h1>
                </div>
                <div className="text-[11px] text-muted-foreground">{visits.length} visits · last 45 days</div>
            </div>

            <div className="grid grid-cols-3 gap-3">
                <Stat icon={Percent} label="Avg Checklist Score" value={`${stats.avgScore}%`} />
                <Stat icon={MapPin} label="Avg Shelf Share" value={`${stats.avgShelf}%`} />
                <Stat icon={Clock} label="Avg Duration" value={`${stats.avgDur} min`} />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="visits-grid">
                {visits.map((v) => (
                    <div
                        key={v.id}
                        data-testid={`visit-${v.id}`}
                        className="hover-lift flex flex-col gap-2 rounded-sm border border-border bg-card p-4"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="text-sm font-semibold">{v.store?.name}</div>
                                <div className="text-[11px] text-muted-foreground">
                                    {v.store?.city} · {v.store?.region}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="mono-num text-[11px] text-muted-foreground">
                                    {v.visit_date?.slice(0, 10)}
                                </div>
                                <div className="mono-num text-[10px] text-muted-foreground">
                                    {v.visit_date?.slice(11, 16)} UTC
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 rounded-sm bg-muted/50 p-2">
                            <StatCell label="Checklist" value={`${v.checklist_score}%`} />
                            <StatCell label="Shelf" value={`${v.shelf_share}%`} />
                            <StatCell label="Duration" value={`${v.duration_minutes}m`} />
                        </div>

                        <div className="text-[11px] text-muted-foreground">
                            <span className="font-medium text-foreground">{v.executive_name}</span> — {v.notes}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span className="mono-num">
                                {v.checkin_lat.toFixed(4)}, {v.checkin_lng.toFixed(4)}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 rounded-sm border border-border bg-card p-3">
            <div className="grid h-9 w-9 place-items-center rounded-sm bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
            </div>
            <div>
                <div className="mlabel">{label}</div>
                <div className="mono-num text-lg font-semibold">{value}</div>
            </div>
        </div>
    );
}

function StatCell({ label, value }) {
    return (
        <div>
            <div className="mlabel text-[9px]">{label}</div>
            <div className="mono-num text-[12px] font-semibold">{value}</div>
        </div>
    );
}
